import React, { useState } from "react";
import axios from "axios";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { Input } from "components/input";
import { Button } from "components/button";
import Spinner from "components/spinner";
import TableComponent from "components/table";
import { useTable } from "components/table";
import { FaExclamationTriangle, FaCheckCircle, FaTimesCircle } from "react-icons/fa";

type MissingPayment = {
  charge_id: string;
  payment_intent_id: string;
  amount: number;
  account: string;
  business_id: number;
  business_name: string;
  created: string;
  metadata: any;
};

type ReprocessResponse = {
  success: boolean;
  dryRun: boolean;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalFound: number;
    totalMissing: number;
    totalProcessed: number;
    totalErrors: number;
  };
  missingPayments: MissingPayment[];
  message: string;
};

const ReprocessPayments: React.FC = () => {
  const { t } = useTranslation();
  const table = useTable();

  const [startDate, setStartDate] = useState("2025-12-14");
  const [endDate, setEndDate] = useState("2025-12-15");
  const [dryRun, setDryRun] = useState(true);
  const [results, setResults] = useState<ReprocessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reprocessMutation = useMutation(
    async ({ startDate, endDate, dryRun }: { startDate: string; endDate: string; dryRun: boolean }) => {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/admin/reprocess-payments`,
        {
          params: {
            startDate,
            endDate,
            dryRun: dryRun.toString(),
          },
        }
      );
      return response.data as ReprocessResponse;
    },
    {
      onSuccess: (data) => {
        setResults(data);
        setError(null);
      },
      onError: (err: any) => {
        setError(err.response?.data?.message || err.message || "Failed to reprocess payments");
        setResults(null);
      },
    }
  );

  const handleReprocess = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date must be before end date");
      return;
    }

    reprocessMutation.mutate({ startDate, endDate, dryRun });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = {
    payment_intent_id: {
      label: "Payment Intent ID",
      cell: (row: MissingPayment) => (
        <div className="font-mono text-sm">{row.payment_intent_id}</div>
      ),
    },
    business_name: {
      label: "Business",
      cell: (row: MissingPayment) => (
        <div className="font-medium">{row.business_name}</div>
      ),
    },
    amount: {
      label: "Amount",
      cell: (row: MissingPayment) => (
        <div className="font-semibold text-green-700">{formatAmount(row.amount)}</div>
      ),
    },
    created: {
      label: "Created",
      cell: (row: MissingPayment) => (
        <div className="text-sm text-gray-600">{formatDate(row.created)}</div>
      ),
    },
    invoice_id: {
      label: "Invoice ID",
      cell: (row: MissingPayment) => (
        <div className="text-sm">
          {row.metadata?.id || row.metadata?.fk_invoice_id || "N/A"}
        </div>
      ),
    },
  };

  return (
    <div className="p-6 max-w-[100rem] mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Reprocess Missing Payments
        </h1>
        <p className="text-gray-600">
          Find and reprocess payments that succeeded in Stripe but weren't recorded in EServ
          during the webhook outage period (Dec 14-15, 2025).
        </p>
      </div>

      {/* Date Range Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate((e.target as HTMLInputElement).value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate((e.target as HTMLInputElement).value)}
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
              />
              <span className="text-sm text-gray-700">Dry Run (find only)</span>
            </label>
          </div>
        </div>

        {!dryRun && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <FaExclamationTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">
                  Processing Mode Enabled
                </h3>
                <p className="mt-1 text-sm text-yellow-700">
                  This will actually process and record missing payments in EServ. Make sure you've reviewed the dry run results first.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4">
          <Button
            onClick={handleReprocess}
            disabled={reprocessMutation.isLoading}
            isLoading={reprocessMutation.isLoading}
            primary
          >
            {dryRun ? "Find Missing Payments" : "Process Missing Payments"}
          </Button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <FaTimesCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      {results && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Results Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-blue-600 font-medium">Total Found</div>
              <div className="text-2xl font-bold text-blue-900">{results.summary.totalFound}</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-sm text-yellow-600 font-medium">Missing</div>
              <div className="text-2xl font-bold text-yellow-900">{results.summary.totalMissing}</div>
            </div>
            {!dryRun && (
              <>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-green-600 font-medium">Processed</div>
                  <div className="text-2xl font-bold text-green-900">{results.summary.totalProcessed}</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-sm text-red-600 font-medium">Errors</div>
                  <div className="text-2xl font-bold text-red-900">{results.summary.totalErrors}</div>
                </div>
              </>
            )}
          </div>
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-700">{results.message}</p>
          </div>
        </div>
      )}

      {/* Missing Payments Table */}
      {results && results.missingPayments.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Missing Payments ({results.missingPayments.length})
            </h2>
            {results.missingPayments.length >= 100 && (
              <p className="text-sm text-gray-500 mt-1">
                Showing first 100 payments. There may be more.
              </p>
            )}
          </div>
          <div className="overflow-x-auto">
            <TableComponent
              data={results.missingPayments}
              columns={columns}
              {...table}
            />
          </div>
        </div>
      )}

      {results && results.missingPayments.length === 0 && results.summary.totalMissing === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center">
            <FaCheckCircle className="mx-auto h-12 w-12 text-green-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No Missing Payments</h3>
            <p className="mt-1 text-sm text-gray-500">
              All payments in the selected date range are already recorded in EServ.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReprocessPayments;

